import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import TimerOffOutlinedIcon from '@mui/icons-material/TimerOffOutlined';
import LockClockOutlinedIcon from '@mui/icons-material/LockClockOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { FirebaseError } from 'firebase/app';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../../firebaseConfig';
import AuthLayout from './AuthLayout';
import AuthStatus from './AuthStatus';
import PasswordField from './PasswordField';
import { authMonitoringService } from '../../services/authMonitoringService';
import { resetPasswordWithToken } from '../../graphqlService';
import { useAuth } from '../../context/AuthContext';
import { isPasswordValid } from '../../utils/passwordPolicy';
import { describeAuthError, getAuthErrorCode } from './authErrors';
import { FIELD_ERRORS, FieldErrors, focusFirstInvalid } from './authForm';
import { backLinkFor, checkResetToken, hasSignedTokenExpired } from './authLinks';

type LinkState = 'checking' | 'ready' | 'invalid' | 'expired' | 'used' | 'done';
type ResetField = 'password' | 'confirmPassword';
const FIELD_ORDER: readonly ResetField[] = ['password', 'confirmPassword'];

const RESET_FAILED_MESSAGE = "We couldn't update your password. Try again, or request a new reset link.";

/**
 * Where a reset link starts: Firebase's own links (?oobCode=) are checked
 * with Firebase before anything is typed; our API's links (?token=) carry
 * their kind and expiry, so a cut-off, wrong or expired link is caught
 * up front too. A link that is fine on paper but already used can only be
 * caught by the API on submit; that keeps the single-use rule server-side.
 */
const initialLinkState = (oobCode: string | null, token: string | null): LinkState => {
  if (oobCode) return 'checking';
  if (token) {
    const problem = checkResetToken(token);
    return problem ?? 'ready';
  }
  return 'invalid';
};

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  // The emailed link can be opened while signed in (after Profile's "Change
  // password"); the way back is then the profile, not sign-in.
  const { user } = useAuth();
  const signedIn = Boolean(user);
  const back = backLinkFor(signedIn);

  const token = searchParams.get('token');
  const email = searchParams.get('email') || '';
  const oobCode = searchParams.get('oobCode');

  const mode = useMemo(() => {
    if (oobCode) return 'firebase';
    if (token) return 'token';
    return 'missing';
  }, [oobCode, token]);

  const [linkState, setLinkState] = useState<LinkState>(() => initialLinkState(oobCode, token));
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors<ResetField>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const refs = {
    password: useRef<HTMLInputElement>(null),
    confirmPassword: useRef<HTMLInputElement>(null),
  };

  useEffect(() => {
    setLinkState(initialLinkState(oobCode, token));
    if (!oobCode) return;

    let cancelled = false;
    verifyPasswordResetCode(auth, oobCode)
      .then(() => {
        if (!cancelled) setLinkState('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const code = getAuthErrorCode(error);
        if (code === 'auth/expired-action-code') setLinkState('expired');
        // Offline: the link may be fine. Show the form; submitting says what failed.
        else if (code === 'auth/network-request-failed') setLinkState('ready');
        else setLinkState('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [oobCode, token]);

  const clearFieldError = (...fields: ResetField[]) => {
    if (fields.some((field) => fieldErrors[field])) {
      setFieldErrors((current) => {
        const next = { ...current };
        fields.forEach((field) => delete next[field]);
        return next;
      });
    }
  };

  const showFieldErrors = (errors: FieldErrors<ResetField>) => {
    setFieldErrors(errors);
    focusFirstInvalid(FIELD_ORDER, errors, refs);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (loading) return;
    setProblem(null);

    const errors: FieldErrors<ResetField> = {};
    if (!password) errors.password = FIELD_ERRORS.newPasswordRequired;
    else if (!isPasswordValid(password)) errors.password = FIELD_ERRORS.newPasswordWeak;
    if (!confirmPassword) errors.confirmPassword = FIELD_ERRORS.confirmRequired;
    else if (password !== confirmPassword) errors.confirmPassword = FIELD_ERRORS.confirmMismatch;
    if (Object.keys(errors).length > 0) {
      showFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    setLoading(true);
    try {
      if (mode === 'firebase' && oobCode) {
        await confirmPasswordReset(auth, oobCode, password);
        setLinkState('done');
        return;
      }

      if (mode === 'token' && token) {
        const result = await resetPasswordWithToken(token, password);
        if (result?.success) {
          setLinkState('done');
          return;
        }
        switch (result?.error) {
          case 'TOKEN_ALREADY_USED':
            setLinkState('used');
            break;
          case 'INVALID_LINK':
            // The API reports an expired link as invalid; say which it is.
            setLinkState(hasSignedTokenExpired(token) ? 'expired' : 'invalid');
            break;
          case 'WEAK_PASSWORD':
            showFieldErrors({ password: FIELD_ERRORS.newPasswordWeak });
            break;
          default:
            setProblem(RESET_FAILED_MESSAGE);
        }
      }
    } catch (error: unknown) {
      authMonitoringService.logLoginFailure(email || 'unknown', error as FirebaseError, 'password_reset_confirm');
      const code = getAuthErrorCode(error);
      if (code === 'auth/expired-action-code') {
        setLinkState('expired');
      } else if (code === 'auth/invalid-action-code') {
        // Firebase uses this for a link that was already used, too.
        setLinkState('invalid');
      } else {
        const copy = describeAuthError(error, 'reset-password');
        if (copy.field === 'password') showFieldErrors({ password: copy.message });
        else setProblem(copy.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const requestNewLink = { label: 'Request a new link', to: '/forgot-password' };

  if (linkState === 'invalid') {
    return (
      <AuthLayout>
        <AuthStatus
          tone="error"
          icon={<LinkOffOutlinedIcon />}
          title="This reset link isn't valid"
          description="It may be incomplete, or a newer link may have replaced it. Request a new link to choose a new password."
          primaryAction={requestNewLink}
          secondaryAction={back}
        />
      </AuthLayout>
    );
  }

  if (linkState === 'expired') {
    return (
      <AuthLayout>
        <AuthStatus
          tone="warning"
          icon={<TimerOffOutlinedIcon />}
          title="This reset link has expired"
          description="Reset links work for 1 hour. Request a new one to choose a new password."
          primaryAction={requestNewLink}
          secondaryAction={back}
        />
      </AuthLayout>
    );
  }

  if (linkState === 'used') {
    return (
      <AuthLayout>
        <AuthStatus
          tone="warning"
          icon={<LockClockOutlinedIcon />}
          title="This reset link has already been used"
          description="Each link works once. If you still need to change your password, request a new link."
          primaryAction={requestNewLink}
          secondaryAction={back}
          focusOnMount
        />
      </AuthLayout>
    );
  }

  if (linkState === 'done') {
    return (
      <AuthLayout>
        <AuthStatus
          tone="success"
          title="Password updated"
          description={
            signedIn
              ? 'Use your new password the next time you sign in.'
              : 'You can now sign in with your new password.'
          }
          primaryAction={signedIn ? back : { label: 'Sign in', to: '/login' }}
          focusOnMount
        />
      </AuthLayout>
    );
  }

  const subtitle = email ? (
    <>
      For{' '}
      <Box component="strong" sx={{ color: 'text.primary', fontWeight: 600 }}>
        {email}
      </Box>
      . Pick a password you don't use anywhere else.
    </>
  ) : (
    "Pick a password you don't use anywhere else."
  );

  return (
    <AuthLayout title="Choose a new password" subtitle={subtitle}>
      {linkState === 'checking' ? (
        <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.secondary' }}>
          <CircularProgress size={18} aria-hidden="true" />
          <Typography variant="body2" color="inherit">
            Checking your reset link…
          </Typography>
        </Box>
      ) : (
        <>
          {problem && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {problem}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'grid', gap: 2.5 }}>
            {/* Lets password managers file the new password under the right account. */}
            {email && <input type="email" name="username" autoComplete="username" value={email} readOnly hidden />}

            <PasswordField
              ref={refs.password}
              id="password"
              name="password"
              label="New password"
              autoComplete="new-password"
              autoFocus
              showRequirements
              required
              InputLabelProps={{ required: false }}
              fullWidth
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                clearFieldError('password', 'confirmPassword');
              }}
              error={Boolean(fieldErrors.password)}
              helperText={fieldErrors.password}
            />

            <PasswordField
              ref={refs.confirmPassword}
              id="confirmPassword"
              name="confirmPassword"
              label="Confirm new password"
              autoComplete="new-password"
              required
              InputLabelProps={{ required: false }}
              fullWidth
              value={confirmPassword}
              onChange={(event) => {
                setConfirmPassword(event.target.value);
                clearFieldError('confirmPassword');
              }}
              error={Boolean(fieldErrors.confirmPassword)}
              helperText={fieldErrors.confirmPassword}
            />

            <Button type="submit" fullWidth variant="contained" size="large" disabled={loading}>
              {loading ? (
                <>
                  <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} aria-hidden="true" />
                  Updating password…
                </>
              ) : (
                'Update password'
              )}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Link
                component={RouterLink}
                to={back.to}
                variant="body2"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontWeight: 600 }}
              >
                <ArrowBackOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
                {back.label}
              </Link>
            </Box>
          </Box>
        </>
      )}
    </AuthLayout>
  );
};

export default ResetPassword;
